import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { ApiError, api } from "../api/client";
import type { Me, Provider } from "../api/types";

interface AuthState {
  me: Me | null;
  providers: Provider[];
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Czy użytkownik ma uprawnienie. Wygoda interfejsu, nie zabezpieczenie —
   *  każde żądanie i tak jest sprawdzane po stronie serwera. */
  can: (code: string) => boolean;
}

const Kontekst = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Kolejność ma znaczenie: /providers ustawia ciasteczko CSRF, bez którego
    // pierwsze logowanie zostałoby odrzucone.
    api
      .get<{ providers: Provider[] }>("/auth/providers")
      .then((d) => setProviders(d.providers))
      .catch(() => setProviders([]))
      .finally(() => {
        api
          .get<Me>("/auth/me")
          .then(setMe)
          .catch(() => setMe(null))
          .finally(() => setLoading(false));
      });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const profil = await api.post<Me>("/auth/login", { username, password });
    setMe(profil);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
    }
    setMe(null);
  }, []);

  const can = useCallback(
    (code: string) => Boolean(me && (me.permissions["*"] || me.permissions[code])),
    [me],
  );

  const wartosc = useMemo(
    () => ({ me, providers, loading, login, logout, can }),
    [me, providers, loading, login, logout, can],
  );
  return <Kontekst.Provider value={wartosc}>{children}</Kontekst.Provider>;
}

export function useAuth(): AuthState {
  const kontekst = useContext(Kontekst);
  if (!kontekst) throw new Error("useAuth poza AuthProvider");
  return kontekst;
}
